/**
 * YouTube URL parser for Watch Together.
 *
 * Handles all common YouTube URL formats and extracts the 11-character video ID.
 */

/** Result of a successful YouTube URL parse. */
export interface ParsedYouTubeUrl {
  videoId: string;
  provider: 'youtube';
}

/**
 * Validate a raw YouTube video ID string.
 * YouTube video IDs are exactly 11 characters: alphanumeric, hyphen, or underscore.
 */
export function isValidYouTubeVideoId(id: string): boolean {
  return /^[a-zA-Z0-9_-]{11}$/.test(id);
}

/**
 * Parse a YouTube URL and extract its video ID.
 *
 * Supported formats:
 * - `https://www.youtube.com/watch?v=VIDEO_ID`
 * - `https://youtube.com/watch?v=VIDEO_ID`
 * - `https://m.youtube.com/watch?v=VIDEO_ID`
 * - `https://youtu.be/VIDEO_ID`
 * - `https://www.youtube.com/embed/VIDEO_ID`
 * - `https://www.youtube.com/shorts/VIDEO_ID`
 * - Any of the above with extra query parameters (e.g. `&t=123`)
 * - Raw 11-character video ID strings (passed through directly)
 *
 * @param url - The URL (or raw video ID) to parse.
 * @returns `ParsedYouTubeUrl` on success, `null` if the input is not a valid YouTube URL.
 */
export function parseYouTubeUrl(url: string): ParsedYouTubeUrl | null {
  if (!url || typeof url !== 'string') return null;

  const trimmed = url.trim();

  // Fast path: raw 11-char video ID passed directly
  if (isValidYouTubeVideoId(trimmed)) {
    return { videoId: trimmed, provider: 'youtube' };
  }

  // Normalise: add a scheme so the URL constructor can parse it
  let normalised = trimmed;
  if (!normalised.startsWith('http://') && !normalised.startsWith('https://')) {
    normalised = 'https://' + normalised;
  }

  let parsed: URL;
  try {
    parsed = new URL(normalised);
  } catch {
    return null;
  }

  const { hostname, pathname, searchParams } = parsed;

  // Accepted hostnames
  const isYouTubeDomain =
    hostname === 'youtube.com' ||
    hostname === 'www.youtube.com' ||
    hostname === 'm.youtube.com' ||
    hostname === 'music.youtube.com';

  const isShortLink = hostname === 'youtu.be';

  if (!isYouTubeDomain && !isShortLink) return null;

  let videoId: string | null = null;

  if (isShortLink) {
    // https://youtu.be/VIDEO_ID
    videoId = pathname.slice(1).split('/')[0] ?? null;
  } else {
    // /watch?v=VIDEO_ID
    if (pathname === '/watch' || pathname.startsWith('/watch?')) {
      videoId = searchParams.get('v');
    }
    // /embed/VIDEO_ID
    else if (pathname.startsWith('/embed/')) {
      videoId = pathname.slice('/embed/'.length).split('?')[0] ?? null;
    }
    // /shorts/VIDEO_ID
    else if (pathname.startsWith('/shorts/')) {
      videoId = pathname.slice('/shorts/'.length).split('?')[0] ?? null;
    }
    // /v/VIDEO_ID  (legacy)
    else if (pathname.startsWith('/v/')) {
      videoId = pathname.slice('/v/'.length).split('?')[0] ?? null;
    }
  }

  if (!videoId) return null;

  // Strip anything after a slash or query separator that may have slipped through
  videoId = videoId.split(/[?&#/]/)[0] ?? '';

  if (!isValidYouTubeVideoId(videoId)) return null;

  return { videoId, provider: 'youtube' };
}
